// 🔊 SYSTÈME DE SONS SATISFAISANTS
// Sons générés via Web Audio API (pas de fichiers externes nécessaires)

class SoundSystem {
  constructor() {
    this.audioContext = null;
    this.enabled = this.loadPreference();
    this.activeOscillators = []; // 🔇 Liste des oscillateurs actifs pour pouvoir les arrêter
  }

  // Charger la préférence depuis localStorage
  loadPreference() {
    try {
      const saved = localStorage.getItem('soundEffectsEnabled');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  }

  // Sauvegarder la préférence
  savePreference(enabled) {
    try {
      localStorage.setItem('soundEffectsEnabled', enabled.toString());
    } catch {}
  }

  // Activer/Désactiver les sons
  setEnabled(enabled) {
    this.enabled = enabled;
    this.savePreference(enabled);
  }

  isEnabled() {
    return this.enabled;
  }

  // Initialiser le contexte audio (doit être appelé après une interaction utilisateur)
  initContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext;
  }

  // 🔇 ARRÊTER TOUS LES SONS EN COURS
  stopAllSounds() {
    // Arrêter tous les oscillateurs actifs
    this.activeOscillators.forEach(osc => {
      try {
        osc.stop();
      } catch (e) {
        // L'oscillateur était peut-être déjà arrêté
      }
    });
    this.activeOscillators = [];
    
    // Créer un nouveau contexte audio propre
    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch (e) {
        // Ignore
      }
      this.audioContext = null;
    }
  }

  // 🔊 Ajouter un oscillateur à la liste des actifs
  trackOscillator(osc) {
    this.activeOscillators.push(osc);
    // Nettoyer automatiquement après 3 secondes max
    setTimeout(() => {
      const index = this.activeOscillators.indexOf(osc);
      if (index > -1) {
        this.activeOscillators.splice(index, 1);
      }
    }, 3000);
  }

  // 🎵 Son de tampon obtenu (ding satisfaisant)
  playStamp() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(880, ctx.currentTime); // La5
    oscillator.frequency.setValueAtTime(1108.73, ctx.currentTime + 0.1); // Do#6
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    
    oscillator.type = 'sine';
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  }

  // 🏆 Son de victoire (fanfare)
  playVictory() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const notes = [523.25, 659.25, 783.99, 1046.50]; // Do Mi Sol Do
    const duration = 0.15;
    
    notes.forEach((freq, i) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime + i * duration);
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime + i * duration);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * duration + duration);
      
      oscillator.type = 'sine';
      oscillator.start(ctx.currentTime + i * duration);
      oscillator.stop(ctx.currentTime + i * duration + duration + 0.1);
    });
  }

  // ⬆️ Son de level up (montée épique)
  playLevelUp() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(300, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.3);
    
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    oscillator.type = 'sawtooth';
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.5);
  }

  // 🎁 Son de cadeau/récompense
  playReward() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const notes = [783.99, 987.77, 1174.66]; // Sol Si Ré
    
    notes.forEach((freq, i) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.2);
      
      oscillator.type = 'sine';
      oscillator.start(ctx.currentTime + i * 0.1);
      oscillator.stop(ctx.currentTime + i * 0.1 + 0.3);
    });
  }

  // 👋 Son de bienvenue (chime doux)
  playWelcome() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const oscillator2 = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // Do
    oscillator2.frequency.setValueAtTime(659.25, ctx.currentTime); // Mi
    
    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    
    oscillator.type = 'sine';
    oscillator2.type = 'sine';
    
    oscillator.start(ctx.currentTime);
    oscillator2.start(ctx.currentTime + 0.1);
    oscillator.stop(ctx.currentTime + 0.8);
    oscillator2.stop(ctx.currentTime + 0.9);
  }

  // ❌ Son d'erreur (doux, pas agressif)
  playError() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(200, ctx.currentTime);
    oscillator.frequency.setValueAtTime(150, ctx.currentTime + 0.1);
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    oscillator.type = 'sine';
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  }

  // 🎰 Son de roue qui tourne
  playSpinning() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Tic-tac rapide
    for (let i = 0; i < 10; i++) {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(800 + Math.random() * 200, ctx.currentTime);
        gain.gain.setValueAtTime(0.1, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.05);
      }, i * 100);
    }
  }

  // 🔔 Son de notification
  playNotification() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(587.33, ctx.currentTime); // Ré
    oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.1); // La
    
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    
    oscillator.type = 'sine';
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  }

  // 🏅 Son de badge débloqué (fanfare épique + shimmer)
  playBadgeUnlocked() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Fanfare de badge (notes montantes épiques)
    const notes = [392, 493.88, 587.33, 783.99, 987.77]; // Sol Si Ré Sol Si
    const duration = 0.12;
    
    notes.forEach((freq, i) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(freq, ctx.currentTime + i * duration);
      gainNode.gain.setValueAtTime(0.25, ctx.currentTime + i * duration);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * duration + duration * 1.5);
      
      oscillator.type = 'triangle';
      oscillator.start(ctx.currentTime + i * duration);
      oscillator.stop(ctx.currentTime + i * duration + duration * 2);
    });
    
    // Shimmer final (son brillant)
    setTimeout(() => {
      const shimmer = ctx.createOscillator();
      const shimmerGain = ctx.createGain();
      shimmer.connect(shimmerGain);
      shimmerGain.connect(ctx.destination);
      
      shimmer.frequency.setValueAtTime(1318.51, ctx.currentTime); // Mi6
      shimmerGain.gain.setValueAtTime(0.15, ctx.currentTime);
      shimmerGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      shimmer.type = 'sine';
      shimmer.start(ctx.currentTime);
      shimmer.stop(ctx.currentTime + 0.5);
    }, notes.length * duration * 1000);
  }

  // 💜 Son de match Heartbeats (romantique)
  playMatch() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Deux notes qui se rejoignent (symbolique du match)
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    const gain2 = ctx.createGain();
    
    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(ctx.destination);
    gain2.connect(ctx.destination);
    
    // Note montante
    osc1.frequency.setValueAtTime(392, ctx.currentTime); // Sol
    osc1.frequency.exponentialRampToValueAtTime(523.25, ctx.currentTime + 0.3); // vers Do
    
    // Note descendante
    osc2.frequency.setValueAtTime(783.99, ctx.currentTime + 0.1); // Sol aigu
    osc2.frequency.exponentialRampToValueAtTime(523.25, ctx.currentTime + 0.4); // vers Do
    
    gain1.gain.setValueAtTime(0.2, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    
    osc1.type = 'sine';
    osc2.type = 'sine';
    
    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime + 0.1);
    osc1.stop(ctx.currentTime + 0.5);
    osc2.stop(ctx.currentTime + 0.6);
  }

  // ✨ Son de clic UI
  playClick() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
    
    oscillator.type = 'sine';
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.05);
  }

  // 🚀 SON DE SAS SPATIAL - Ouverture épique de portail !
  playSasOpen() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Son de pressurisation/décompression
    const noise = ctx.createOscillator();
    const noiseGain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(200, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.5);
    filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 1);
    
    noise.frequency.setValueAtTime(80, ctx.currentTime);
    noise.type = 'sawtooth';
    
    noiseGain.gain.setValueAtTime(0.3, ctx.currentTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.2);
    
    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 1.2);
    
    // Son de servo/mécanisme hydraulique
    setTimeout(() => {
      const servo = ctx.createOscillator();
      const servoGain = ctx.createGain();
      servo.connect(servoGain);
      servoGain.connect(ctx.destination);
      
      servo.frequency.setValueAtTime(150, ctx.currentTime);
      servo.frequency.linearRampToValueAtTime(300, ctx.currentTime + 0.3);
      servo.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.5);
      
      servoGain.gain.setValueAtTime(0.15, ctx.currentTime);
      servoGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
      
      servo.type = 'square';
      servo.start(ctx.currentTime);
      servo.stop(ctx.currentTime + 0.6);
    }, 200);
    
    // Chime de confirmation finale
    setTimeout(() => {
      const chime = ctx.createOscillator();
      const chimeGain = ctx.createGain();
      chime.connect(chimeGain);
      chimeGain.connect(ctx.destination);
      
      chime.frequency.setValueAtTime(880, ctx.currentTime);
      chimeGain.gain.setValueAtTime(0.2, ctx.currentTime);
      chimeGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      
      chime.type = 'sine';
      chime.start(ctx.currentTime);
      chime.stop(ctx.currentTime + 0.4);
    }, 800);
  }

  // 🛸 SON DE WARP/HYPERESPACE - Transition dimensionnelle !
  playWarp() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Son de warp montant
    const warp = ctx.createOscillator();
    const warpGain = ctx.createGain();
    const warpFilter = ctx.createBiquadFilter();
    
    warp.connect(warpFilter);
    warpFilter.connect(warpGain);
    warpGain.connect(ctx.destination);
    
    warpFilter.type = 'bandpass';
    warpFilter.frequency.setValueAtTime(200, ctx.currentTime);
    warpFilter.frequency.exponentialRampToValueAtTime(4000, ctx.currentTime + 0.8);
    warpFilter.Q.setValueAtTime(5, ctx.currentTime);
    
    warp.frequency.setValueAtTime(100, ctx.currentTime);
    warp.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.8);
    warp.type = 'sawtooth';
    
    warpGain.gain.setValueAtTime(0.25, ctx.currentTime);
    warpGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
    
    warp.start(ctx.currentTime);
    warp.stop(ctx.currentTime + 1);
    
    // Écho spatial
    setTimeout(() => {
      const echo = ctx.createOscillator();
      const echoGain = ctx.createGain();
      echo.connect(echoGain);
      echoGain.connect(ctx.destination);
      
      echo.frequency.setValueAtTime(1500, ctx.currentTime);
      echo.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.5);
      
      echoGain.gain.setValueAtTime(0.1, ctx.currentTime);
      echoGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      echo.type = 'sine';
      echo.start(ctx.currentTime);
      echo.stop(ctx.currentTime + 0.5);
    }, 600);
  }

  // 🌟 SON DE MAGIE SPATIALE - Pour Pixie !
  playSpaceMagic() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Arpège magique spatial
    const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51]; // Do Mi Sol Do Mi
    
    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        
        filter.type = 'highpass';
        filter.frequency.setValueAtTime(500, ctx.currentTime);
        
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        osc.frequency.setValueAtTime(freq * 1.02, ctx.currentTime + 0.05); // Léger vibrato
        osc.frequency.setValueAtTime(freq, ctx.currentTime + 0.1);
        
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        
        osc.type = 'sine';
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      }, i * 80);
    });
    
    // Shimmer final
    setTimeout(() => {
      const shimmer = ctx.createOscillator();
      const shimmerGain = ctx.createGain();
      shimmer.connect(shimmerGain);
      shimmerGain.connect(ctx.destination);
      
      shimmer.frequency.setValueAtTime(2000, ctx.currentTime);
      shimmer.frequency.exponentialRampToValueAtTime(4000, ctx.currentTime + 0.3);
      
      shimmerGain.gain.setValueAtTime(0.08, ctx.currentTime);
      shimmerGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      shimmer.type = 'sine';
      shimmer.start(ctx.currentTime);
      shimmer.stop(ctx.currentTime + 0.5);
    }, 500);
  }

  // 🔮 SON DE PORTAIL DIMENSIONNEL - Entrée dans l'univers LPE !
  playPortalOpen() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Basse profonde de portail
    const bass = ctx.createOscillator();
    const bassGain = ctx.createGain();
    bass.connect(bassGain);
    bassGain.connect(ctx.destination);
    
    bass.frequency.setValueAtTime(60, ctx.currentTime);
    bass.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 1.5);
    bass.type = 'sine';
    
    bassGain.gain.setValueAtTime(0.4, ctx.currentTime);
    bassGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);
    
    bass.start(ctx.currentTime);
    bass.stop(ctx.currentTime + 1.5);
    
    // Swoosh ascendant
    const swoosh = ctx.createOscillator();
    const swooshGain = ctx.createGain();
    const swooshFilter = ctx.createBiquadFilter();
    
    swoosh.connect(swooshFilter);
    swooshFilter.connect(swooshGain);
    swooshGain.connect(ctx.destination);
    
    swooshFilter.type = 'bandpass';
    swooshFilter.frequency.setValueAtTime(500, ctx.currentTime);
    swooshFilter.frequency.exponentialRampToValueAtTime(3000, ctx.currentTime + 0.6);
    swooshFilter.Q.setValueAtTime(2, ctx.currentTime);
    
    swoosh.frequency.setValueAtTime(200, ctx.currentTime);
    swoosh.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.6);
    swoosh.type = 'sawtooth';
    
    swooshGain.gain.setValueAtTime(0.2, ctx.currentTime);
    swooshGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    
    swoosh.start(ctx.currentTime);
    swoosh.stop(ctx.currentTime + 0.8);
    
    // Notes magiques de confirmation
    setTimeout(() => {
      const notes = [659.25, 783.99, 1046.50]; // Mi Sol Do
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        
        osc.type = 'sine';
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.3);
      });
    }, 500);
  }

  // ⚡ SON DE CHOIX/SÉLECTION - Click spatial satisfaisant
  playSpaceSelect() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3000, ctx.currentTime);
    
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.setValueAtTime(1200, ctx.currentTime + 0.05);
    osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.15);
    
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    
    osc.type = 'sine';
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  }

  // 🎉 SON DE SUCCÈS SPATIAL - Email capturé !
  playSpaceSuccess() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Fanfare spatiale
    const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98]; // Do Mi Sol Do Mi Sol
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.08);
      gain.gain.setValueAtTime(0.2 - i * 0.02, ctx.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.08 + 0.3);
      
      osc.type = i < 3 ? 'triangle' : 'sine';
      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + i * 0.08 + 0.4);
    });
    
    // Shimmer final épique
    setTimeout(() => {
      for (let i = 0; i < 3; i++) {
        const shimmer = ctx.createOscillator();
        const shimmerGain = ctx.createGain();
        shimmer.connect(shimmerGain);
        shimmerGain.connect(ctx.destination);
        
        shimmer.frequency.setValueAtTime(2000 + i * 500, ctx.currentTime);
        shimmerGain.gain.setValueAtTime(0.08, ctx.currentTime);
        shimmerGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
        
        shimmer.type = 'sine';
        shimmer.start(ctx.currentTime + i * 0.1);
        shimmer.stop(ctx.currentTime + i * 0.1 + 0.6);
      }
    }, 600);
  }

  // ===========================================
  // 🎮 SONS SURPRISE - ACCUEIL UNIQUE À CHAQUE CONNEXION !
  // ===========================================

  // 🎮 Son style COIN (Mario-like) - Pièce collectée !
  playCoin() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.frequency.setValueAtTime(988, ctx.currentTime); // Si5
    osc.frequency.setValueAtTime(1319, ctx.currentTime + 0.1); // Mi6
    
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    
    osc.type = 'square';
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  }

  // 🏃 Son style RING (Sonic-like) - Anneau collecté !
  playRing() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const notes = [1047, 1319, 1568]; // Do6 Mi6 Sol6
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.05);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.05 + 0.15);
      
      osc.type = 'triangle';
      osc.start(ctx.currentTime + i * 0.05);
      osc.stop(ctx.currentTime + i * 0.05 + 0.2);
    });
  }

  // 🎯 Son POWER-UP épique !
  playPowerUp() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    // Montée rapide de fréquence (style rétro)
    osc.frequency.setValueAtTime(200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.15);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.2);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.35);
    
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    osc.type = 'square';
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  }

  // 🐕 Son de CHIEN content (woof woof !)
  playDogBark() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Deux aboiements
    [0, 0.25].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(800, ctx.currentTime + delay);
      filter.Q.setValueAtTime(5, ctx.currentTime + delay);
      
      osc.frequency.setValueAtTime(350, ctx.currentTime + delay);
      osc.frequency.exponentialRampToValueAtTime(250, ctx.currentTime + delay + 0.1);
      
      gain.gain.setValueAtTime(0.4, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.15);
      
      osc.type = 'sawtooth';
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.15);
    });
  }

  // 🐱 Son de CHAT content (miaou !)
  playCatMeow() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1500, ctx.currentTime);
    filter.Q.setValueAtTime(3, ctx.currentTime);
    
    // Miaulement montant puis descendant
    osc.frequency.setValueAtTime(500, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.2);
    osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.5);
    
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.setValueAtTime(0.35, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
    
    osc.type = 'sine';
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.6);
  }

  // 🎺 Son FANFARE ROYALE (ta-da !)
  playFanfare() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    const notes = [392, 392, 392, 523, 392, 523, 659]; // Sol Sol Sol Do Sol Do Mi
    const durations = [0.15, 0.15, 0.15, 0.4, 0.2, 0.2, 0.6];
    let time = 0;
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(freq, ctx.currentTime + time);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + time);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + time + durations[i] * 0.9);
      
      osc.type = 'sawtooth';
      osc.start(ctx.currentTime + time);
      osc.stop(ctx.currentTime + time + durations[i]);
      
      time += durations[i];
    });
  }

  // 🪄 Son MAGIQUE (sparkles !)
  playMagicSparkle() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Cascade de notes scintillantes
    const notes = [1047, 1175, 1319, 1397, 1568, 1760, 1976, 2093];
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.06);
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.06 + 0.3);
      
      osc.type = 'sine';
      osc.start(ctx.currentTime + i * 0.06);
      osc.stop(ctx.currentTime + i * 0.06 + 0.35);
    });
  }

  // 🎪 Son CIRCUS (ta-daaa !)
  playCircus() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Pattern de cirque
    const pattern = [
      { freq: 523, dur: 0.1 },
      { freq: 587, dur: 0.1 },
      { freq: 659, dur: 0.1 },
      { freq: 698, dur: 0.1 },
      { freq: 784, dur: 0.3 },
      { freq: 784, dur: 0.15 },
      { freq: 698, dur: 0.15 },
      { freq: 784, dur: 0.4 },
    ];
    
    let time = 0;
    pattern.forEach(({ freq, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(freq, ctx.currentTime + time);
      gain.gain.setValueAtTime(0.2, ctx.currentTime + time);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + time + dur * 0.9);
      
      osc.type = 'triangle';
      osc.start(ctx.currentTime + time);
      osc.stop(ctx.currentTime + time + dur);
      
      time += dur;
    });
  }

  // 🚀 Son ROCKET LAUNCH (décollage !)
  playRocketLaunch() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Bruit de propulseur
    const noise = ctx.createOscillator();
    const noiseGain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(500, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(3000, ctx.currentTime + 0.8);
    
    noise.frequency.setValueAtTime(100, ctx.currentTime);
    noise.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.8);
    
    noiseGain.gain.setValueAtTime(0.3, ctx.currentTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
    
    noise.type = 'sawtooth';
    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 1);
    
    // Notes épiques de décollage
    setTimeout(() => {
      const notes = [261, 329, 392, 523];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
        gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.3);
        
        osc.type = 'sine';
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.4);
      });
    }, 400);
  }

  // 🎰 Son JACKPOT (cha-ching !)
  playJackpot() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // Son de pièces qui tombent
    for (let i = 0; i < 8; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      const freq = 2000 + Math.random() * 2000;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.05);
      
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.05 + 0.1);
      
      osc.type = 'sine';
      osc.start(ctx.currentTime + i * 0.05);
      osc.stop(ctx.currentTime + i * 0.05 + 0.15);
    }
    
    // Fanfare finale
    setTimeout(() => {
      const notes = [523, 659, 784, 1047];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.1);
        gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.4);
        
        osc.type = 'triangle';
        osc.start(ctx.currentTime + i * 0.1);
        osc.stop(ctx.currentTime + i * 0.1 + 0.5);
      });
    }, 400);
  }

  // 🚪 Son SAS DE VAISSEAU QUI S'OUVRE - "HUUUUUG... TIN TONG !"
  playAirlockOpen() {
    if (!this.enabled) return;
    const ctx = this.initContext();
    
    // === PARTIE 1: "HUUUUUG" - Dépressurisation/Air qui s'échappe ===
    const noise = ctx.createOscillator();
    const noiseGain = ctx.createGain();
    const noiseFilter = ctx.createBiquadFilter();
    
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    
    // Filtre passe-bas qui monte (effet de sas qui s'ouvre)
    noiseFilter.type = 'lowpass';
    noiseFilter.frequency.setValueAtTime(200, ctx.currentTime);
    noiseFilter.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.3);
    noiseFilter.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.8);
    noiseFilter.Q.setValueAtTime(5, ctx.currentTime);
    
    noise.frequency.setValueAtTime(80, ctx.currentTime);
    noise.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.2);
    noise.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.8);
    noise.type = 'sawtooth';
    
    noiseGain.gain.setValueAtTime(0.4, ctx.currentTime);
    noiseGain.gain.setValueAtTime(0.5, ctx.currentTime + 0.2);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
    
    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 1);
    
    // Sifflement d'air (couche supplémentaire)
    const hiss = ctx.createOscillator();
    const hissGain = ctx.createGain();
    const hissFilter = ctx.createBiquadFilter();
    
    hiss.connect(hissFilter);
    hissFilter.connect(hissGain);
    hissGain.connect(ctx.destination);
    
    hissFilter.type = 'bandpass';
    hissFilter.frequency.setValueAtTime(3000, ctx.currentTime);
    hissFilter.frequency.exponentialRampToValueAtTime(1000, ctx.currentTime + 0.8);
    hissFilter.Q.setValueAtTime(2, ctx.currentTime);
    
    hiss.frequency.setValueAtTime(100, ctx.currentTime);
    hiss.type = 'sawtooth';
    
    hissGain.gain.setValueAtTime(0.15, ctx.currentTime);
    hissGain.gain.setValueAtTime(0.25, ctx.currentTime + 0.15);
    hissGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.9);
    
    hiss.start(ctx.currentTime);
    hiss.stop(ctx.currentTime + 0.9);
    
    // === PARTIE 2: "TIN TONG" - Bip de confirmation ===
    setTimeout(() => {
      // TIN (note haute)
      const tin = ctx.createOscillator();
      const tinGain = ctx.createGain();
      tin.connect(tinGain);
      tinGain.connect(ctx.destination);
      
      tin.frequency.setValueAtTime(1200, ctx.currentTime);
      tin.type = 'sine';
      
      tinGain.gain.setValueAtTime(0.3, ctx.currentTime);
      tinGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      
      tin.start(ctx.currentTime);
      tin.stop(ctx.currentTime + 0.15);
      
      // TONG (note plus basse, légèrement après)
      setTimeout(() => {
        const tong = ctx.createOscillator();
        const tongGain = ctx.createGain();
        tong.connect(tongGain);
        tongGain.connect(ctx.destination);
        
        tong.frequency.setValueAtTime(800, ctx.currentTime);
        tong.type = 'sine';
        
        tongGain.gain.setValueAtTime(0.35, ctx.currentTime);
        tongGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
        
        tong.start(ctx.currentTime);
        tong.stop(ctx.currentTime + 0.25);
      }, 180);
    }, 900);
  }

  // 🎊 SON SURPRISE ALÉATOIRE - Un son différent à chaque fois !
  // Ou le son préféré de l'utilisateur s'il en a choisi un !
  playRandomWelcome() {
    if (!this.enabled) return;
    
    // Liste des sons disponibles
    const soundOptions = {
      coin: { fn: () => this.playCoin(), name: '🪙 Coin !' },
      ring: { fn: () => this.playRing(), name: '💍 Ring !' },
      powerup: { fn: () => this.playPowerUp(), name: '⚡ Power-Up !' },
      dog: { fn: () => this.playDogBark(), name: '🐕 Woof !' },
      cat: { fn: () => this.playCatMeow(), name: '🐱 Miaou !' },
      fanfare: { fn: () => this.playFanfare(), name: '🎺 Fanfare !' },
      magic: { fn: () => this.playMagicSparkle(), name: '✨ Magic !' },
      circus: { fn: () => this.playCircus(), name: '🎪 Ta-daa !' },
      rocket: { fn: () => this.playRocketLaunch(), name: '🚀 Décollage !' },
      jackpot: { fn: () => this.playJackpot(), name: '🎰 Jackpot !' },
      victory: { fn: () => this.playVictory(), name: '🏆 Victory !' },
      levelup: { fn: () => this.playLevelUp(), name: '⬆️ Level Up !' },
      airlock: { fn: () => this.playAirlockOpen(), name: '🚪 Sas Spatial !' },
    };
    
    // Vérifier si l'utilisateur a un son préféré
    let preferredSound = null;
    try {
      preferredSound = localStorage.getItem('preferredWelcomeSound');
    } catch {
      // localStorage non disponible
    }
    
    // Si un son préféré est défini (et ce n'est pas "random"), le jouer
    if (preferredSound && preferredSound !== 'random' && soundOptions[preferredSound]) {
      soundOptions[preferredSound].fn();
      return soundOptions[preferredSound].name;
    }
    
    // Sinon, jouer un son aléatoire
    const soundKeys = Object.keys(soundOptions);
    const randomKey = soundKeys[Math.floor(Math.random() * soundKeys.length)];
    soundOptions[randomKey].fn();
    return soundOptions[randomKey].name;
  }
}

// Instance singleton
const soundSystem = new SoundSystem();

export default soundSystem;
export { SoundSystem };
